import { Lightbulb, X, FlaskConical, Droplets, Beaker, TestTube, Eye, AlertCircle } from 'lucide-react'

const infoData = {
  1: {
    title: 'Labeling Your Samples',
    science: 'In scientific experiments, we always compare a CONTROL (unchanged) with an EXPERIMENTAL (treated) sample. This is called the scientific method - it allows us to isolate the effect of our treatment.',
    whatHappens: 'You are setting up two identical starting conditions. The control beaker will remain untreated throughout the experiment, serving as your baseline for comparison.',
    observe: [
      'Control = No treatment (shows natural settling)',
      'Experimental = Will receive lime + alum treatment',
      'Both start with identical contaminated water'
    ],
    diagram: 'labeling'
  },
  2: {
    title: 'Adding Water Sample',
    science: 'We use approximately 200mL of water to simulate a real-world sample. In water treatment plants, millions of liters are processed using the same principles you will learn.',
    whatHappens: 'The beaker fills with clear water. Notice the meniscus (curved surface) at the top - this is caused by adhesion between water molecules and glass.',
    observe: [
      'Water level should reach the 200mL mark',
      'The meniscus curves upward at the edges',
      'Water appears clear before contamination'
    ],
    diagram: 'water'
  },
  3: {
    title: 'Adding Soil Contaminants',
    science: 'Soil contains suspended particles (clay, silt), organic matter, and dissolved minerals. These particles have a NEGATIVE electrical charge, which is why they repel each other and stay suspended instead of settling.',
    whatHappens: 'The soil disperses throughout the water, creating turbidity (cloudiness). The tiny particles are too small to settle on their own due to Brownian motion and electrostatic repulsion.',
    observe: [
      'Water becomes brown/muddy immediately',
      'Particles remain suspended (don\'t settle)',
      'Light cannot pass through easily (turbid)'
    ],
    diagram: 'soil'
  },
  4: {
    title: 'Stirring for Homogeneity',
    science: 'Stirring ensures HOMOGENEOUS distribution - every part of the sample has the same concentration of contaminants. This is critical for fair comparison between control and experimental samples.',
    whatHappens: 'Mechanical agitation distributes particles evenly. Without stirring, heavier particles might concentrate at the bottom, giving unequal samples when divided.',
    observe: [
      'Color becomes uniform throughout',
      'No settling visible during stirring',
      'Vortex forms in center of liquid'
    ],
    diagram: 'stir'
  },
  5: {
    title: 'Creating Control & Experimental Samples',
    science: 'By dividing the contaminated water equally, we ensure both samples start IDENTICAL. Any difference we observe later can only be attributed to the treatment we apply.',
    whatHappens: 'Half the muddy water transfers to the control beaker. Both beakers now contain ~100mL of identically contaminated water.',
    observe: [
      'Both beakers have equal volume (~100mL)',
      'Both have identical color/turbidity',
      'Control will receive NO treatment'
    ],
    diagram: 'pour'
  },
  6: {
    title: 'Adding Lime (Calcium Oxide)',
    science: 'Lime (CaO) reacts with water in an EXOTHERMIC reaction:\n\nCaO + H₂O → Ca(OH)₂ + Heat\n\nThis creates calcium hydroxide, making the solution BASIC (pH > 7). The basic environment is essential for the next step.',
    whatHappens: 'The lime dissolves and reacts with water. You may notice slight warming. The solution becomes alkaline, which will help the alum work effectively.',
    observe: [
      'Slight cloudiness as lime dissolves',
      'Solution may feel slightly warm',
      'pH increases (becomes basic)',
      'Prepare for litmus test to confirm'
    ],
    diagram: 'lime',
    equation: 'CaO + H₂O → Ca²⁺ + 2OH⁻'
  },
  7: {
    title: 'Testing pH with Litmus Paper',
    science: 'Litmus is a natural pH indicator extracted from lichens. It contains compounds that change color based on hydrogen ion concentration:\n• Acidic (pH < 7): Stays RED\n• Basic (pH > 7): Turns BLUE',
    whatHappens: 'When red litmus paper contacts the basic solution, the OH⁻ ions cause a chemical change in the litmus dye molecules, shifting their light absorption to appear blue.',
    observe: [
      'Red litmus paper turns BLUE',
      'Color change confirms pH > 7 (basic)',
      'This confirms lime reacted successfully',
      'Basic pH is required for coagulation'
    ],
    diagram: 'litmus'
  },
  8: {
    title: 'Adding Alum (Coagulant)',
    science: 'Alum [Al₂(SO₄)₃] dissociates in water to release Al³⁺ ions. In basic conditions, these form aluminum hydroxide:\n\nAl³⁺ + 3OH⁻ → Al(OH)₃↓\n\nThe Al(OH)₃ precipitate is POSITIVELY charged and attracts the NEGATIVELY charged dirt particles!',
    whatHappens: 'This is COAGULATION - opposite charges attract! The positive Al(OH)₃ flocs attach to negative dirt particles, neutralizing their charge so they can clump together (FLOCCULATION).',
    observe: [
      'White, fluffy precipitate forms',
      'Particles begin clumping together',
      'Solution may look more cloudy initially',
      'Flocs (clumps) become visible'
    ],
    diagram: 'alum',
    equation: 'Al³⁺ + 3OH⁻ → Al(OH)₃↓ (precipitate)'
  },
  9: {
    title: 'Sedimentation (Settling)',
    science: 'Gravity pulls the heavy flocs downward - this is SEDIMENTATION. The rate of settling follows Stokes\' Law: larger, denser particles settle faster. The flocs we created are much larger than individual dirt particles.',
    whatHappens: 'Over time, the flocs sink to the bottom, forming a layer of SEDIMENT. The water above gradually becomes clearer as particles are removed.',
    observe: [
      'Flocs slowly sink to bottom',
      'Sediment layer forms at base',
      'Water clarity improves from top down',
      'Process takes 30+ minutes in real labs'
    ],
    diagram: 'settle'
  },
  10: {
    title: 'Comparing Results',
    science: 'This is the critical evaluation step. The CONTROL shows what happens naturally (minimal settling due to particle repulsion). The EXPERIMENTAL shows the effect of coagulation treatment.',
    whatHappens: 'Compare both beakers side by side. The treated water should be significantly clearer, demonstrating that coagulation and flocculation successfully removed suspended particles.',
    observe: [
      'CONTROL: Still turbid, minimal settling',
      'EXPERIMENTAL: Much clearer water',
      'EXPERIMENTAL: Visible sediment layer',
      'Treatment removed suspended solids!'
    ],
    lookFor: [
      { label: 'Clarity', control: 'Cloudy/turbid', experimental: 'Clear or nearly clear' },
      { label: 'Sediment', control: 'Little to none', experimental: 'Thick layer at bottom' },
      { label: 'Color', control: 'Brown throughout', experimental: 'Light/clear on top' }
    ],
    diagram: 'compare'
  }
}

// SVG Diagrams for visualization
const Diagrams = {
  labeling: () => (
    <svg viewBox="0 0 200 80" className="info-diagram">
      <rect x="20" y="20" width="60" height="50" rx="4" fill="#3A3A3A" stroke="#666" strokeWidth="2"/>
      <text x="50" y="50" textAnchor="middle" fill="#888" fontSize="10">Control</text>
      <rect x="120" y="20" width="60" height="50" rx="4" fill="#3A3A3A" stroke="#FF5A36" strokeWidth="2"/>
      <text x="150" y="50" textAnchor="middle" fill="#FF5A36" fontSize="10">Experimental</text>
      <text x="100" y="12" textAnchor="middle" fill="#999" fontSize="8">↔ Identical starting conditions</text>
    </svg>
  ),
  soil: () => (
    <svg viewBox="0 0 200 80" className="info-diagram">
      <rect x="60" y="15" width="80" height="55" rx="4" fill="#4A3A2A" stroke="#666" strokeWidth="2"/>
      {[...Array(15)].map((_, i) => (
        <circle key={i} cx={75 + (i % 5) * 15} cy={30 + Math.floor(i / 5) * 15} r="3" fill="#2A1A0A" opacity="0.7">
          <animate attributeName="cy" values={`${30 + Math.floor(i / 5) * 15};${33 + Math.floor(i / 5) * 15};${30 + Math.floor(i / 5) * 15}`} dur="2s" repeatCount="indefinite"/>
        </circle>
      ))}
      <text x="100" y="78" textAnchor="middle" fill="#FF5A36" fontSize="8">Negative charges repel → stay suspended</text>
      {[70, 90, 110, 130].map((x, i) => (
        <text key={i} x={x} y="25" fill="#EF4444" fontSize="8">−</text>
      ))}
    </svg>
  ),
  lime: () => (
    <svg viewBox="0 0 200 80" className="info-diagram">
      <text x="20" y="25" fill="#FFF" fontSize="11" fontFamily="monospace">CaO</text>
      <text x="55" y="25" fill="#888" fontSize="11">+</text>
      <text x="70" y="25" fill="#7DD3FC" fontSize="11" fontFamily="monospace">H₂O</text>
      <text x="105" y="25" fill="#888" fontSize="11">→</text>
      <text x="120" y="25" fill="#00A86B" fontSize="11" fontFamily="monospace">Ca²⁺</text>
      <text x="155" y="25" fill="#888" fontSize="11">+</text>
      <text x="165" y="25" fill="#8B5CF6" fontSize="11" fontFamily="monospace">2OH⁻</text>
      <rect x="50" y="40" width="100" height="30" rx="4" fill="none" stroke="#00A86B" strokeWidth="2" strokeDasharray="4"/>
      <text x="100" y="58" textAnchor="middle" fill="#00A86B" fontSize="9">Basic solution (pH &gt; 7)</text>
    </svg>
  ),
  litmus: () => (
    <svg viewBox="0 0 200 80" className="info-diagram">
      <rect x="30" y="20" width="50" height="40" rx="2" fill="#DC2626"/>
      <text x="55" y="45" textAnchor="middle" fill="#FFF" fontSize="10">RED</text>
      <text x="55" y="70" textAnchor="middle" fill="#999" fontSize="8">Before</text>
      <path d="M90 40 L110 40" stroke="#888" strokeWidth="2" markerEnd="url(#arrow)"/>
      <text x="100" y="35" textAnchor="middle" fill="#888" fontSize="8">+ OH⁻</text>
      <rect x="120" y="20" width="50" height="40" rx="2" fill="#3B82F6"/>
      <text x="145" y="45" textAnchor="middle" fill="#FFF" fontSize="10">BLUE</text>
      <text x="145" y="70" textAnchor="middle" fill="#00A86B" fontSize="8">Basic!</text>
      <defs>
        <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#888"/>
        </marker>
      </defs>
    </svg>
  ),
  alum: () => (
    <svg viewBox="0 0 200 100" className="info-diagram">
      <text x="100" y="15" textAnchor="middle" fill="#FFEA00" fontSize="10">COAGULATION</text>
      <circle cx="50" cy="50" r="15" fill="none" stroke="#EF4444" strokeWidth="2"/>
      <text x="50" y="54" textAnchor="middle" fill="#EF4444" fontSize="14">−</text>
      <text x="50" y="75" textAnchor="middle" fill="#999" fontSize="7">Dirt particle</text>
      <circle cx="100" cy="50" r="12" fill="#00A86B" opacity="0.3"/>
      <circle cx="100" cy="50" r="8" fill="none" stroke="#00A86B" strokeWidth="2"/>
      <text x="100" y="54" textAnchor="middle" fill="#00A86B" fontSize="14">+</text>
      <text x="100" y="75" textAnchor="middle" fill="#999" fontSize="7">Al(OH)₃</text>
      <path d="M68 50 L85 50" stroke="#FFEA00" strokeWidth="2" strokeDasharray="3"/>
      <g transform="translate(150, 50)">
        <circle r="20" fill="#5A4A3A" opacity="0.5"/>
        <circle cx="-5" cy="-5" r="8" fill="none" stroke="#EF4444" strokeWidth="1.5"/>
        <circle cx="5" cy="5" r="6" fill="none" stroke="#00A86B" strokeWidth="1.5"/>
        <text x="0" y="35" textAnchor="middle" fill="#FF5A36" fontSize="7">FLOC</text>
      </g>
    </svg>
  ),
  settle: () => (
    <svg viewBox="0 0 200 100" className="info-diagram">
      <rect x="60" y="10" width="80" height="70" rx="4" fill="#3A4A5A" stroke="#666" strokeWidth="2"/>
      {/* Clear water on top */}
      <rect x="62" y="12" width="76" height="40" fill="#7DD3FC" opacity="0.4"/>
      {/* Sediment at bottom */}
      <rect x="62" y="55" width="76" height="23" fill="#8B7355"/>
      {/* Settling particles */}
      {[0,1,2].map(i => (
        <circle key={i} cx={80 + i * 20} cy="35" r="4" fill="#5A4A3A">
          <animate attributeName="cy" values="35;55;55" dur="2s" repeatCount="indefinite" begin={`${i * 0.3}s`}/>
          <animate attributeName="opacity" values="1;1;0" dur="2s" repeatCount="indefinite" begin={`${i * 0.3}s`}/>
        </circle>
      ))}
      <path d="M145 30 L155 30 L155 50 L165 50" fill="none" stroke="#999" strokeWidth="1"/>
      <text x="168" y="35" fill="#7DD3FC" fontSize="8">Clear</text>
      <text x="168" y="53" fill="#8B7355" fontSize="8">Sediment</text>
      <text x="100" y="95" textAnchor="middle" fill="#FF5A36" fontSize="8">↓ Gravity pulls flocs down</text>
    </svg>
  ),
  compare: () => (
    <svg viewBox="0 0 220 100" className="info-diagram">
      {/* Control beaker */}
      <rect x="20" y="20" width="70" height="60" rx="4" fill="#6B5B4B" stroke="#666" strokeWidth="2"/>
      <text x="55" y="90" textAnchor="middle" fill="#888" fontSize="9">CONTROL</text>
      <text x="55" y="55" textAnchor="middle" fill="#FFF" fontSize="8">Still cloudy</text>
      {/* Experimental beaker */}
      <rect x="130" y="20" width="70" height="60" rx="4" fill="#3A4A5A" stroke="#00A86B" strokeWidth="2"/>
      <rect x="132" y="22" width="66" height="40" fill="#B8E6F2" opacity="0.6"/>
      <rect x="132" y="62" width="66" height="16" fill="#8B7355"/>
      <text x="165" y="90" textAnchor="middle" fill="#00A86B" fontSize="9">TREATED</text>
      <text x="165" y="40" textAnchor="middle" fill="#FFF" fontSize="8">Clear!</text>
      {/* Comparison arrow */}
      <text x="110" y="55" textAnchor="middle" fill="#FF5A36" fontSize="16">→</text>
    </svg>
  )
}

const InfoBox = ({ step, experimentId, stepInfo, onClose }) => {
  // Use stepInfo from config if provided, otherwise fall back to hardcoded data (for exp2 backward compatibility)
  let data;
  if (stepInfo) {
    // Convert stepInfo from config format to InfoBox format
    data = {
      title: stepInfo.title,
      science: stepInfo.content,
      whatHappens: stepInfo.whatHappens || '',
      observe: stepInfo.observe || [],
      diagram: stepInfo.diagram,
      equation: stepInfo.equation,
      lookFor: stepInfo.lookFor
    };
  } else if (experimentId === 'exp2') {
    // Fall back to hardcoded data for Water Purification experiment
    data = infoData[step];
  }

  if (!data) return null

  const DiagramComponent = Diagrams[data.diagram]

  return (
    <div className="infobox-overlay" onClick={onClose}>
      <div className="infobox" onClick={e => e.stopPropagation()}>
        <button className="infobox-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="infobox-header">
          <div className="infobox-icon">
            <Lightbulb size={24} />
          </div>
          <div>
            <span className="step-badge">Step {step}</span>
            <h3>{data.title}</h3>
          </div>
        </div>

        <div className="infobox-content">
          {/* Scientific Explanation */}
          <div className="infobox-section science">
            <div className="section-header">
              <FlaskConical size={16} />
              <span className="section-label">THE SCIENCE</span>
            </div>
            <p>{data.science}</p>
            {data.equation && (
              <div className="equation-box">
                <code>{data.equation}</code>
              </div>
            )}
          </div>

          {/* Visual Diagram */}
          {DiagramComponent && (
            <div className="infobox-diagram">
              <DiagramComponent />
            </div>
          )}

          {/* What Happens */}
          {data.whatHappens && (
            <div className="infobox-section happens">
              <div className="section-header">
                <Droplets size={16} />
                <span className="section-label">WHAT HAPPENS</span>
              </div>
              <p>{data.whatHappens}</p>
            </div>
          )}

          {/* What to Observe */}
          {data.observe && data.observe.length > 0 && (
            <div className="infobox-section observe">
              <div className="section-header">
                <Eye size={16} />
                <span className="section-label">WHAT TO OBSERVE</span>
              </div>
              <ul>
                {data.observe.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Comparison Table (for step 10) */}
          {data.lookFor && (
            <div className="infobox-compare">
              <table>
                <thead>
                  <tr>
                    <th>Observation</th>
                    <th>Control</th>
                    <th>Treated</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lookFor.map((row, i) => (
                    <tr key={i}>
                      <td>{row.label}</td>
                      <td className="control">{row.control}</td>
                      <td className="experimental">{row.experimental}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <button className="infobox-btn" onClick={onClose}>
          I Understand!
        </button>
      </div>

      <style>{`
        .infobox-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(2px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease-out;
          padding: 20px;
        }

        .infobox {
          background: var(--bg-surface);
          border-radius: 16px;
          padding: 24px;
          width: 560px;
          max-width: 95vw;
          max-height: 90vh;
          overflow-y: auto;
          border: 2px solid var(--primary);
          position: relative;
        }

        .infobox-close {
          position: absolute;
          top: 16px;
          right: 16px;
          color: var(--text-muted);
          transition: color 0.2s;
          z-index: 10;
        }

        .infobox-close:hover {
          color: var(--text-primary);
        }

        .infobox-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 20px;
        }

        .infobox-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .step-badge {
          display: inline-block;
          background: var(--primary);
          color: white;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 10px;
          margin-bottom: 4px;
        }

        .infobox-header h3 {
          font-size: 20px;
          font-weight: 700;
          line-height: 1.2;
        }

        .infobox-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 20px;
        }

        .infobox-section {
          background: var(--bg-elevated);
          border-radius: 10px;
          padding: 14px;
          border-left: 4px solid;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .section-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .infobox-section.science {
          border-color: var(--accent);
        }
        .infobox-section.science .section-header { color: var(--accent); }

        .infobox-section.happens {
          border-color: var(--secondary);
        }
        .infobox-section.happens .section-header { color: var(--secondary); }

        .infobox-section.observe {
          border-color: var(--primary-light);
        }
        .infobox-section.observe .section-header { color: var(--primary-light); }

        .infobox-section p {
          font-size: 13px;
          line-height: 1.6;
          color: var(--text-primary);
          white-space: pre-line;
        }

        .infobox-section ul {
          margin: 0;
          padding-left: 20px;
        }

        .infobox-section li {
          font-size: 13px;
          line-height: 1.6;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .equation-box {
          background: var(--bg-dark);
          border-radius: 6px;
          padding: 10px 14px;
          margin-top: 10px;
          text-align: center;
        }

        .equation-box code {
          font-family: 'Fira Code', monospace;
          font-size: 14px;
          color: var(--accent);
        }

        .infobox-diagram {
          background: var(--bg-dark);
          border-radius: 10px;
          padding: 12px;
          display: flex;
          justify-content: center;
        }

        .info-diagram {
          width: 100%;
          max-width: 280px;
          height: auto;
        }

        .infobox-compare {
          background: var(--bg-elevated);
          border-radius: 10px;
          overflow: hidden;
        }

        .infobox-compare table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .infobox-compare th,
        .infobox-compare td {
          padding: 10px 12px;
          text-align: left;
          border-bottom: 1px solid var(--border);
        }

        .infobox-compare th {
          background: var(--bg-dark);
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .infobox-compare .control {
          color: var(--text-muted);
        }

        .infobox-compare .experimental {
          color: var(--secondary);
          font-weight: 500;
        }

        .infobox-btn {
          width: 100%;
          padding: 14px;
          background: var(--primary);
          color: white;
          border-radius: 10px;
          font-weight: 600;
          font-size: 15px;
          transition: transform 0.15s, box-shadow 0.15s;
        }

        .infobox-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 90, 54, 0.4);
        }
      `}</style>
    </div>
  )
}

export default InfoBox
